import React, { useState, useEffect } from 'react';
import './ShippingInfoInputModal.css';
import TopBar from '../TopBar/TopBar';
import DropdownIcon from '../../../assets/icons/icon_dropdown.svg'; // Import the SVG icon
import { Country, State, City, ICountry, IState, ICity } from 'country-state-city';
import { updateShippingInfo } from '../../../api/profileServices';
import VIToast from '../VIToast/VIToast';

interface ShippingInfoInputModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialShippingInfo: {
    name?: string;
    email?: string;
    phoneNumber?: string;
    addressLine1?: string;
    addressLine2?: string;
    country?: string;
    state?: string;
    city?: string;
    zipcode?: string;
  } | null;
  onUpdateComplete: (success: boolean) => void;
}

const ShippingInfoInputModal: React.FC<ShippingInfoInputModalProps> = ({ isOpen, onClose, initialShippingInfo, onUpdateComplete }) => {
  const [countries, setCountries] = useState<ICountry[]>([]);
  const [states, setStates] = useState<IState[]>([]);
  const [cities, setCities] = useState<ICity[]>([]);
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedState, setSelectedState] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [customCountry, setCustomCountry] = useState('');
  const [customState, setCustomState] = useState('');
  const [customCity, setCustomCity] = useState('');
  const [name, setName] = useState(initialShippingInfo?.name || '');
  const [email, setEmail] = useState(initialShippingInfo?.email || '');
  const [phoneNumber, setPhoneNumber] = useState(initialShippingInfo?.phoneNumber || '');
  const [addressLine1, setAddressLine1] = useState(initialShippingInfo?.addressLine1 || '');
  const [addressLine2, setAddressLine2] = useState(initialShippingInfo?.addressLine2 || '');
  const [zipcode, setZipcode] = useState(initialShippingInfo?.zipcode || '');
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);

  useEffect(() => {
    setCountries(Country.getAllCountries());
  }, []);

  useEffect(() => {
    if (selectedCountry) {
      setStates(State.getStatesOfCountry(selectedCountry));
    }
  }, [selectedCountry]);

  useEffect(() => {
    if (selectedState) {
      setCities(City.getCitiesOfState(selectedCountry, selectedState));
    }
  }, [selectedState]);

  useEffect(() => {
    if (initialShippingInfo) {
      setSelectedCountry(initialShippingInfo.country ?? '');
      setSelectedState(initialShippingInfo.state ?? '');
      setSelectedCity(initialShippingInfo.city ?? '');
    }
  }, [initialShippingInfo]);

  useEffect(() => {
    const isValid = 
      !!name && 
      !!email && 
      !!phoneNumber && 
      !!addressLine1 && 
      !!(selectedCountry || customCountry) && 
      !!(selectedState || customState) && 
      !!(selectedCity || customCity) && 
      !!zipcode;
    
    setIsFormValid(isValid);
  }, [
    name, 
    email, 
    phoneNumber, 
    addressLine1, 
    selectedCountry, 
    customCountry,
    selectedState, 
    customState,
    selectedCity, 
    customCity,
    zipcode
  ]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    const shippingData = {
      first_name: name.split(' ')[0],
      last_name: name.includes(' ') ? name.substring(name.indexOf(' ') + 1) : '',
      address_line_1: addressLine1,
      address_line_2: addressLine2,
      city: selectedCity === 'custom' ? customCity : selectedCity,
      state: selectedState === 'custom' ? customState : selectedState,
      postal_code: zipcode,
      country: selectedCountry === 'custom' ? customCountry : selectedCountry,
      phone_number: phoneNumber
    };

    try {
      await updateShippingInfo(shippingData);
      onUpdateComplete(true);
      onClose();
    } catch (error) {
      console.error('Failed to update shipping info:', error);
      setToastMessage('Failed to update shipping information');
      setShowToast(true);
      onUpdateComplete(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const validateForm = (): boolean => {
    if (!name) {
      setToastMessage('Please enter your name');
      setShowToast(true);
      return false;
    }
    if (!email) {
      setToastMessage('Please enter your email');
      setShowToast(true);
      return false;
    }
    if (!phoneNumber) {
      setToastMessage('Please enter your phone number');
      setShowToast(true);
      return false;
    }
    if (!addressLine1) {
      setToastMessage('Please enter your address');
      setShowToast(true);
      return false;
    }
    if (!selectedCountry && !customCountry) {
      setToastMessage('Please select your country');
      setShowToast(true);
      return false;
    }
    if (!selectedState && !customState) {
      setToastMessage('Please select your state');
      setShowToast(true);
      return false;
    }
    if (!selectedCity && !customCity) {
      setToastMessage('Please select your city');
      setShowToast(true);
      return false;
    }
    if (!zipcode) {
      setToastMessage('Please enter your zipcode');
      setShowToast(true);
      return false;
    }
    return true;
  };

  if (!isOpen) return null;

  return (
    <div className="shipping-modal-overlay">
      {showToast && (
        <VIToast
          message={toastMessage}
          onClose={() => setShowToast(false)}
        />
      )}
      <div className="shipping-modal-content">
        <TopBar 
          showBackButton={true} 
          showLogo={false} 
          pageName="" 
          onBackClick={onClose} 
        />
        <button className="close-button" onClick={onClose}>Done</button>
        <h2>Shipping</h2>
        <p>Fill out your details for more opportunities.</p>
        <form className="shipping-form" onSubmit={handleSubmit}>
          <div className="info-group">
            <div className="shipping-form-group">
              <label className="input-title">Name*</label>
              <input 
                className={`shipping-form-input ${name ? 'input-filled' : ''}`} 
                type="text" 
                placeholder="Full name" 
                value={name}
                onChange={(e) => setName(e.target.value)} 
              />
            </div>
            <div className="shipping-form-group">
              <label className="input-title">Email*</label>
              <input 
                className={`shipping-form-input ${email ? 'input-filled' : ''}`} 
                type="email" 
                placeholder="Email" 
                value={email}
                onChange={(e) => setEmail(e.target.value)} 
              />
            </div>
            <div className="shipping-form-group">
              <label className="input-title">Phone number*</label>
              <input 
                className={`shipping-form-input ${phoneNumber ? 'input-filled' : ''}`} 
                type="text" 
                placeholder="Write phone number" 
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)} 
              />
            </div>
          </div>

          <div className="info-group">
            <div className="shipping-form-group">
              <label className="input-title">Address Line*</label>
              <input 
                className={`shipping-form-input ${addressLine1 ? 'input-filled' : ''}`} 
                type="text" 
                placeholder="Street address, P.O box" 
                value={addressLine1}
                onChange={(e) => setAddressLine1(e.target.value)} 
              />
              <input 
                className={`shipping-form-input ${addressLine2 ? 'input-filled' : ''}`} 
                type="text" 
                placeholder="Apt, suite, unit, building, floor" 
                value={addressLine2}
                onChange={(e) => setAddressLine2(e.target.value)} 
              />
            </div>
            <div className="shipping-form-group">
              <label className="input-title">Country*</label>
              <div className="select-wrapper">
                <select 
                  className={`shipping-form-select ${selectedCountry ? 'input-filled' : ''}`} 
                  onChange={(e) => setSelectedCountry(e.target.value)}
                >
                  <option>Select country</option>
                  {countries.map((country) => (
                    <option key={country.isoCode} value={country.isoCode}>
                      {country.name}
                    </option>
                  ))}
                  <option value="custom">Other (Specify below)</option>
                </select>
                <img src={DropdownIcon} alt="Dropdown Icon" className="dropdown-icon" />
              </div>
              {selectedCountry === 'custom' && (
                <input
                  className={`shipping-form-input ${customCountry ? 'input-filled' : ''}`}
                  type="text"
                  placeholder="Enter custom country"
                  value={customCountry}
                  onChange={(e) => setCustomCountry(e.target.value)}
                />
              )}
            </div>
            <div className="shipping-form-group">
              <label className="input-title">State*</label>
              <div className="select-wrapper">
                <select 
                  className={`shipping-form-select ${selectedState ? 'input-filled' : ''}`} 
                  onChange={(e) => setSelectedState(e.target.value)}
                >
                  <option>Select state</option>
                  {selectedCountry && selectedCountry !== 'custom' ? (
                    states.map((state) => (
                      <option key={state.isoCode} value={state.isoCode}>
                        {state.name}
                      </option>
                    ))
                  ) : (
                    <option>Please select country</option>
                  )}
                  <option value="custom">Other (Specify below)</option>
                </select>
                <img src={DropdownIcon} alt="Dropdown Icon" className="dropdown-icon" />
              </div>
              {selectedState === 'custom' && (
                <input
                  className={`shipping-form-input ${customState ? 'input-filled' : ''}`}
                  type="text"
                  placeholder="Enter custom state"
                  value={customState}
                  onChange={(e) => setCustomState(e.target.value)}
                />
              )}
            </div>
            <div className="shipping-form-group">
              <label className="input-title">City*</label>
              <div className="select-wrapper">
                <select 
                  className={`shipping-form-select ${selectedCity ? 'input-filled' : ''}`} 
                  onChange={(e) => setSelectedCity(e.target.value)}
                >
                  <option>Select city</option>
                  {selectedState && selectedState !== 'custom' ? (
                    cities.map((city) => (
                      <option key={city.name} value={city.name}>
                        {city.name}
                      </option>
                    ))
                  ) : (
                    <option>Please select state</option>
                  )}
                  <option value="custom">Other (Specify below)</option>
                </select>
                <img src={DropdownIcon} alt="Dropdown Icon" className="dropdown-icon" />
              </div>
              {selectedCity === 'custom' && (
                <input
                  className={`shipping-form-input ${customCity ? 'input-filled' : ''}`}
                  type="text"
                  placeholder="Enter custom city"
                  value={customCity}
                  onChange={(e) => setCustomCity(e.target.value)}
                />
              )}
            </div>
            <div className="shipping-form-group">
              <label className="input-title">Zipcode*</label>
              <input 
                className={`shipping-form-input ${zipcode ? 'input-filled' : ''}`} 
                type="text" 
                placeholder="Write zipcode" 
                value={zipcode}
                onChange={(e) => setZipcode(e.target.value)} 
              />
            </div>
          </div>

          <button 
            type="submit" 
            className={`submit-button ${isSubmitting ? 'submitting' : ''} ${!isFormValid ? 'disabled' : ''}`}
            disabled={isSubmitting || !isFormValid}
          >
            {isSubmitting ? 'Submitting...' : 'Done'}
          </button>
        </form>
      </div>
      

    </div>
  );
};

export default ShippingInfoInputModal;